import React from 'react';
import { motion } from 'framer-motion';
import { Check, ChevronRight, Phone } from 'lucide-react';
import { type CallService, callServiceHref } from '../../lib/callServices';

/**
 * 블랭킷 전화 상담 서비스 하나를 정식 서비스처럼 카드 하나로 보여 준다 (2026-10-22 대표 18:3x:
 * "서비스당 기준 정식 서비스들처럼 하나의 카드로 설명을 해줘야 되고, 정말 매력적으로 최고의 것들만").
 * 팔로워 레이더 카드와 같은 골격 — 머리(아이콘·이름·큰 제목) → 첫 설명 → 이럴 때 → 매력 세 가지 → 시작하기·무료 3분·번호.
 * 글은 모두 lib/callServices(각 사이트 문장). 카드를 누르면 그 사이트가 새 탭으로 열린다(출처 꼬리표는 자리마다).
 */
export const CallServiceCard: React.FC<{ service: CallService; placement: 'home' | 'dashboard'; index?: number }> = ({ service: s, placement, index = 0 }) => (
    <motion.a
        href={callServiceHref(s, placement)}
        target="_blank"
        rel="noopener noreferrer"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 0.5, delay: Math.min(index, 3) * 0.06 }}
        whileHover={{ y: -2 }}
        className="group relative block"
    >
        {/* 배경 글로우 */}
        <div className="absolute inset-0 rounded-3xl blur-2xl" style={{ background: `${s.accent}14` }} />

        <div
            className="relative rounded-3xl border bg-black/50 p-6 shadow-2xl backdrop-blur-xl transition-colors sm:p-8"
            style={{ borderColor: `${s.accent}33` }}
        >
            {/* 상단 하이라이트 라인 */}
            <div className="pointer-events-none absolute inset-x-6 top-0 h-px" style={{ background: `linear-gradient(to right, transparent, ${s.accent}80, transparent)` }} />

            <div className="mb-4 flex items-start gap-3">
                <div
                    className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border sm:h-12 sm:w-12"
                    style={{ background: `${s.accent}1a`, borderColor: `${s.accent}40` }}
                >
                    <s.icon className="h-5 w-5 sm:h-6 sm:w-6" style={{ color: s.accent }} />
                </div>
                <div className="min-w-0">
                    <p className="text-xs font-semibold sm:text-sm" style={{ color: s.accent }}>{s.name} · {s.kind}</p>
                    <h2 className="mt-0.5 text-lg font-bold leading-snug tracking-[-0.01em] text-white break-keep sm:text-xl lg:text-2xl">{s.tagline}</h2>
                </div>
            </div>

            <p className="text-sm leading-relaxed text-[#A8A8A8] break-keep">{s.lead}</p>

            <div className="mt-4 rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3">
                <p className="text-[11px] font-semibold" style={{ color: s.accent }}>{s.moment.label}</p>
                <p className="mt-1 text-sm leading-relaxed text-white/90 break-keep">“{s.moment.text}”</p>
            </div>

            <ul className="mt-4 space-y-2">
                {s.points.map((pt) => (
                    <li key={pt.text} className="flex items-start gap-2 text-sm leading-relaxed text-[#A8A8A8] break-keep">
                        <Check className="mt-0.5 h-4 w-4 shrink-0" style={{ color: s.accent }} />
                        <span>
                            {pt.title && <span className="font-semibold text-white">{pt.title} </span>}
                            {pt.text}
                        </span>
                    </li>
                ))}
            </ul>

            <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <span
                    className="inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-white/10 px-6 py-3.5 text-sm font-semibold text-[#0b0b0b] transition-shadow sm:w-auto sm:text-base"
                    style={{ background: s.accent, boxShadow: `0 0 28px ${s.accent}40` }}
                >
                    {s.name} 시작하기
                    <ChevronRight className="h-4 w-4 transition-transform group-hover:translate-x-1 sm:h-5 sm:w-5" />
                </span>
                <span className="inline-flex items-center justify-center gap-1.5 text-xs text-[#A8A8A8] sm:justify-end">
                    <Phone className="h-3.5 w-3.5" style={{ color: s.accent }} />
                    <span>처음 3분 무료 · 가입·카드 없이 ·</span>
                    <span className="font-semibold tabular-nums text-white">{s.phone ?? '전화번호 준비 중'}</span>
                </span>
            </div>
        </div>
    </motion.a>
);
